import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { Pause, Plus } from 'lucide-react';

import { MicrostoreElementState, useGetSiteQuery, TotadminPermission } from 'data/__generated__';

import { Loader } from 'components/Loader';
import {
    EntityPageBreadcrumb,
    PageActions,
    PageActionsMenu,
    PageHeader,
    type PageActionGroup,
} from 'components/PageHeader';
import { CreateMicrostoreColumnGroupPopup } from 'pages/MicrostoreColumnGroups/Popups/CreateMicrostoreColumnGroupPopup';

import { Button } from '@/components/ui/button';
import {
    Tree,
    TreeActions,
    TreeLabel,
    TreeLeaf,
    TreeNode,
    TreeNodeChildren,
    TreeNodeTrigger,
} from '@/components/ui/tree';

import { ColumnNodeActions } from './ColumnNodeActions';
import { ColumnGroupNodeActions } from './ColumnGroupNodeActions';

type ParamTypes = {
    siteId: string;
};

const STATE_BADGE: Record<MicrostoreElementState, string> = {
    [MicrostoreElementState.Regular]: 'bg-emerald-100 text-emerald-900 border-emerald-300',
    [MicrostoreElementState.Disabled]: 'bg-amber-100 text-amber-900 border-amber-300',
    [MicrostoreElementState.Archived]: 'bg-zinc-100 text-zinc-600 border-zinc-300',
};

export const SiteMicrostoreColumnGroups = () => {
    const { siteId = '' } = useParams<ParamTypes>();

    const [createColumnGroupOpen, setCreateColumnGroupOpen] = useState(false);

    const { data, loading, error, refetch } = useGetSiteQuery({
        variables: { siteId },
    });

    if (loading) {
        return (
            <Container>
                <CenteredLoader>
                    <Loader />
                </CenteredLoader>
            </Container>
        );
    }

    if (error || !data?.site) {
        throw new Error('Une erreur est survenue lors de la récupération du site');
    }

    const site = data.site;
    const totem = site.totem;
    const organization = totem.organization;

    const sortedGroups = [...site.microstoreColumnGroups].sort((a, b) => a.positionInSite - b.positionInSite);
    const totalColumns = sortedGroups.reduce((sum, group) => sum + group.columns.length, 0);

    const handleMutated = () => {
        refetch();
    };

    const handleCreateColumnGroupClose = (open: boolean) => {
        setCreateColumnGroupOpen(open);
        if (!open) handleMutated();
    };

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Outils',
            items: [
                { label: 'Éditeur legacy (planogramme)', to: `/microstore-column-groups/${site._id}` },
                {
                    label: 'Planogramme visuel',
                    to: `/planogram/${site._id}`,
                    requiredPermissions: [TotadminPermission.MicrostoreColumnsRead],
                },
                {
                    label: 'Ouvrir le site',
                    to: `/site/${site._id}`,
                    requiredPermissions: [TotadminPermission.SitesRead],
                },
            ],
        },
    ];

    return (
        <Container>
            <Helmet title={`${site.name} · Arborescence microstore`} />
            <PageHeader>
                <EntityPageBreadcrumb organization={organization} totem={totem} site={site} microstoreView />
                <PageActions>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setCreateColumnGroupOpen(true)}
                        data-test="add-column-group-button"
                        requiredPermissions={[TotadminPermission.MicrostoreColumnGroupsManage]}
                    >
                        <Plus />
                        Ajouter un groupe
                    </Button>
                    <PageActionsMenu groups={actionGroups} />
                </PageActions>
            </PageHeader>
            <Content data-test="site-microstore-column-groups">
                <Header>
                    <Title data-test="page-title">{site.name} · Arborescence microstore</Title>
                    <Subtitle>
                        {sortedGroups.length} groupe{sortedGroups.length > 1 ? 's' : ''} · {totalColumns} colonne
                        {totalColumns > 1 ? 's' : ''}
                    </Subtitle>
                </Header>
                {sortedGroups.length === 0 ? (
                    <Empty data-test="empty-state">Aucun groupe configuré sur ce site.</Empty>
                ) : (
                    <TreeCard data-test="column-groups-tree">
                        <Tree>
                            {sortedGroups.map((group) => {
                                const sortedColumns = [...group.columns].sort(
                                    (a, b) => a.positionInColumnGroup - b.positionInColumnGroup,
                                );
                                return (
                                    <TreeNode key={group._id} defaultOpen data-test={`column-group-${group._id}`}>
                                        <TreeNodeTrigger>
                                            <TreeLabel>
                                                <span className="font-semibold" data-test="column-group-name">
                                                    {group.name}
                                                </span>
                                                <span className="text-xs text-muted-foreground">
                                                    {sortedColumns.length} colonne
                                                    {sortedColumns.length > 1 ? 's' : ''}
                                                </span>
                                            </TreeLabel>
                                            <TreeActions>
                                                <ColumnGroupNodeActions columnGroup={group} onMutated={handleMutated} />
                                            </TreeActions>
                                        </TreeNodeTrigger>
                                        <TreeNodeChildren>
                                            {sortedColumns.length === 0 ? (
                                                <li className="px-1.5 py-1 text-xs text-muted-foreground">
                                                    Aucune colonne
                                                </li>
                                            ) : (
                                                sortedColumns.map((column) => (
                                                    <TreeLeaf key={column._id} data-test={`column-${column._id}`}>
                                                        <TreeLabel>
                                                            <span className="w-7 shrink-0 font-mono text-xs text-muted-foreground">
                                                                #{column.positionInColumnGroup + 1}
                                                            </span>
                                                            <span
                                                                className="truncate font-medium"
                                                                data-test="column-name"
                                                            >
                                                                {column.name}
                                                            </span>
                                                            <span className="hidden truncate text-xs text-muted-foreground sm:inline">
                                                                {column.microstoreColumnTemplateName}
                                                            </span>
                                                            <StateBadge
                                                                className={STATE_BADGE[column.state]}
                                                                data-test="column-state"
                                                            >
                                                                {column.state}
                                                            </StateBadge>
                                                            {column.isPaused ? (
                                                                <span
                                                                    className="inline-flex items-center gap-0.5 rounded-full border border-amber-300 bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-900"
                                                                    data-test="column-paused-badge"
                                                                >
                                                                    <Pause className="size-2.5" />
                                                                    En pause
                                                                </span>
                                                            ) : null}
                                                        </TreeLabel>
                                                        <TreeActions>
                                                            <ColumnNodeActions
                                                                columnId={column._id}
                                                                columnName={column.name}
                                                                isPaused={column.isPaused}
                                                            />
                                                        </TreeActions>
                                                    </TreeLeaf>
                                                ))
                                            )}
                                        </TreeNodeChildren>
                                    </TreeNode>
                                );
                            })}
                        </Tree>
                    </TreeCard>
                )}
            </Content>
            <CreateMicrostoreColumnGroupPopup
                isOpen={createColumnGroupOpen}
                setIsOpen={handleCreateColumnGroupClose}
                site={site}
            />
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    padding: 15px;
    gap: 15px;
    flex: 1;
    overflow-y: auto;
`;

const Header = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const Title = styled.h2`
    font-size: 18px;
    font-weight: 600;
    margin: 0;
`;

const Subtitle = styled.span`
    font-size: 12px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const TreeCard = styled.div`
    display: flex;
    flex-direction: column;
    padding: 12px;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: var(--radius-lg);
    background-color: ${({ theme }) => theme.cardBackgroundColor};
`;

const StateBadge = styled.span`
    border-radius: 9999px;
    border-width: 1px;
    padding: 1px 8px;
    font-size: 10px;
    font-weight: 500;
`;

const Empty = styled.div`
    color: ${({ theme }) => theme.infoTextColor};
    font-size: 14px;
`;

const CenteredLoader = styled.div`
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
`;
